import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import {
  Address,
  createAddress,
  IAktionen,
  IdCard,
  IDifferentInvoiceRecipient,
  IERechnungsInfo,
  IOlavEntry,
  IOrderDetailsInfo,
  IProduktAuswahlDetails,
  IProduktVerfuegbarkeit,
  IRelocationData,
  IUploadResult,
  IVertragskonto,
  parseAddress,
  parseILCustomerAddress,
  PreisIndikation,
  PriceSheet,
  Product,
  RelocationPDFLinks,
  TariffSelectionCardData,
} from '@mwe/models';
import {
  getAccountOwner,
  getFormattedContactAddress,
  getInvoiceEmail,
  getOrderDetailWithDifferentInvoiceRecipient,
  hasClearingAccountSepaMandant,
  isCancelledRelocationOrMoveOut,
  isEmailEmpty,
  isProductCategoryFernwaerme,
  isProductCategoryStromOrGas,
  sortProductsByCategory,
} from '@mwe/utils';
import {
  ProductCategoryEnum,
  RELOCATION_ERROR_ROUTE,
  RELOCATION_NEW_ADDRESS_ROUTE,
  RELOCATION_ROUTE,
  RELOCATION_STATUS_ROUTE,
  ServiceStateEnum,
} from '@mwe/constants';
import { Router } from '@angular/router';
import { checkIfRelocationIsAllowed, updateRelocationDetails } from './relocation.utils';
import { ConfirmationStatusState, withConfirmationStatusState } from '../store-features/confirmation-status-state.feature';
import { withStorageServiceFeature } from '../store-features/storage-service.feature';
import { MemoryCacheItem } from '../cache/memory-cache.service';
import { ProductsWhiteListLogic } from '../products/products-white-list.logic';
import { AddressGroupService } from '../address/address-group.service';

const STORAGE_KEY = 'relocation';

export type RelocationStoreType = InstanceType<typeof RelocationStore>;

const initialState = {
  relocationFromAddress: null as Address,
  relocationToAddress: null as Address,
  relocationToOlav: null as IOlavEntry,
  relocationToAvailabilities: null as IProduktVerfuegbarkeit[],
  registrationDate: null as string,
  deregistrationDate: null as string,
  relocationDetails: null as IOrderDetailsInfo[],
  idCard: null as IdCard,
  uploadedFiles: null as IUploadResult,
  useSubstituteDetails: null as boolean,
  pdfLinks: null as RelocationPDFLinks,
  noOnlineCategories: null as string[],
  selectedTariffs: null as TariffSelectionCardData[],
  relocationFromProducts: null as Product[],
  relocationInitializedForFromAddress: null as boolean,
  addressDetailsLinkType: null as string,
  relocationToAvailabilitiesState: null as ServiceStateEnum,
  previousSelectedProducts: null as ProductCategoryEnum[],
  noLongerAvailableCategories: null as string[],
  approvalDataTransferToStromOrGas: null as boolean,
  relocationAddressSetTo: null as boolean,
  deliveryOfContractDataToNewAddress: null as boolean,
  selectedProducts: null as IProduktAuswahlDetails[],
  selectedProductsPriceIndications: null as PreisIndikation[],
  allPriceIndications: null as PreisIndikation[],
  allSelectedProducts: null as IProduktAuswahlDetails[],
  customerDataChangeData: null as { email: string; customerNumbers: string[] },
  invoiceData: null as IVertragskonto,
  eRechnungsInfo: null as IERechnungsInfo,
  _differentInvoiceRecipient: null as IDifferentInvoiceRecipient,
  eRechnungChangeIndices: null as number[],
  sepaChangeIndices: null as number[],
  priceSheets: null as PriceSheet[],
  voucherInfo: null as IAktionen,
};

type InitialRelocationState = typeof initialState;

export const RelocationStore = signalStore(
  { providedIn: 'root' },
  withConfirmationStatusState({
    _memoryCacheItem: MemoryCacheItem.ClearingAccountDetails,
  }),
  withStorageServiceFeature(STORAGE_KEY),
  withProps(() => ({
    _productsWhiteListLogic: inject(ProductsWhiteListLogic),
    _addressGroupService: inject(AddressGroupService),
    _router: inject(Router),
  })),
  withState(initialState),
  withComputed(store => {
    const relocationData = computed<IRelocationData>(() => ({
      relocationFromAddress: store.relocationFromAddress(),
      relocationToAddress: store.relocationToAddress(),
      relocationToOlav: store.relocationToOlav(),
      registrationDate: store.registrationDate(),
      deregistrationDate: store.deregistrationDate(),
      relocationDetails: store.relocationDetails(),
      idCard: store.idCard(),
      uploadedFiles: store.uploadedFiles(),
      useSubstituteDetails: store.useSubstituteDetails(),
      pdfLinks: store.pdfLinks(),
      noOnlineCategories: store.noOnlineCategories(),
      selectedTariffs: store.selectedTariffs(),
      eRechnungChangeIndices: store.eRechnungChangeIndices(),
      sepaChangeIndices: store.sepaChangeIndices(),
      priceSheets: store.priceSheets(),
    }));

    const uniqueCustomerNumbers = computed(() => {
      const numbers = [...new Set(store.relocationDetails()?.map(rd => rd.customerNumber + '_' + rd.accountNumber))];
      return numbers?.toSorted((a, b) => (a < b ? -1 : 1));
    });

    const hasValidRelocationProduct = computed(() => {
      if (store.addressDetailsLinkType()) {
        return false;
      }

      return !!store
        ?.relocationFromProducts()
        ?.find(p => isProductCategoryStromOrGas(p.category) || isProductCategoryFernwaerme(p.category));
    });

    const hasOnlyFW = computed(() => {
      if (store.addressDetailsLinkType()) {
        return false;
      }

      return store
        .relocationDetails()
        ?.filter(item => item.status !== 'Abmeldung')
        ?.every(item => isProductCategoryFernwaerme(item.category));
    });

    const invoiceInfoData = computed(() => {
      const orderDetails = store.relocationDetails();
      if (!orderDetails || orderDetails.length === 0) return null;

      const orderDetailsInvoiceList = orderDetails.filter(order => hasClearingAccountSepaMandant(order.verrechnungskonto));
      const orderDetailsERechnungList = orderDetails.filter(order => !!getInvoiceEmail(order.verrechnungskonto));

      const customerNumbers = new Set(
        orderDetails
          .map(o => {
            const isFW = isProductCategoryFernwaerme(o.category);
            if (isFW || o.status !== 'Anmeldung') return null;
            const accountRelation = getAccountOwner(o.verrechnungskonto?.geschaeftspartnerBeziehungen);

            return !isEmailEmpty(accountRelation?.email) ? o.customerNumber : null;
          })
          .filter(Boolean),
      );

      const commonAccountNumbers = new Set(
        orderDetailsInvoiceList
          .map(o1 => orderDetailsERechnungList.find(o2 => o2.accountNumber === o1.accountNumber)?.accountNumber ?? null)
          .filter(Boolean),
      );

      let selectedInvoice = orderDetailsInvoiceList.at(0) ?? null;
      let selectedERechnung = orderDetailsERechnungList.at(0) ?? null;

      if (commonAccountNumbers.size > 0) {
        selectedInvoice = orderDetailsInvoiceList.find(o => commonAccountNumbers.has(o.accountNumber)) ?? selectedInvoice;
        selectedERechnung = orderDetailsERechnungList.find(o => commonAccountNumbers.has(o.accountNumber)) ?? selectedERechnung;
      }

      return {
        orderDetails,
        selectedInvoice,
        selectedERechnung,
        customerNumbers: [...customerNumbers],
        hashCode: store.relocationFromAddress()?.hashCode,
      };
    });

    const differentInvoiceRecipient = computed<IDifferentInvoiceRecipient>(() => {
      const _differentInvoiceRecipient = store._differentInvoiceRecipient();
      const relocationDetails = store.relocationDetails();
      const _hasOnlyFW = hasOnlyFW();
      const relocationToAddress = store.relocationToAddress();

      if (_differentInvoiceRecipient) return _differentInvoiceRecipient;
      if (!relocationDetails) return null;

      const orderDetails = getOrderDetailWithDifferentInvoiceRecipient(relocationDetails);
      if (!orderDetails || _hasOnlyFW) return null;

      return {
        physischePerson: orderDetails.physischePerson,
        invoiceAddress: parseAddress(relocationToAddress),
        organisation: orderDetails.organisation,
      };
    });

    const formattedDifferentInvoiceAddress = computed<string>(() => {
      const _differentInvoiceRecipient = differentInvoiceRecipient();
      if (!_differentInvoiceRecipient) return '';

      return getFormattedContactAddress(
        _differentInvoiceRecipient.physischePerson,
        parseILCustomerAddress(_differentInvoiceRecipient.invoiceAddress),
        _differentInvoiceRecipient.organisation,
      );
    });

    const hasOnlyFwInDeregistration = computed(() => {
      const deregistration = store.relocationDetails()?.filter(order => order.status === 'Abmeldung') ?? [];
      return deregistration.every(order => isProductCategoryFernwaerme(order.category));
    });

    return {
      relocationData,
      uniqueCustomerNumbers,
      hasValidRelocationProduct,
      hasOnlyFW,
      invoiceInfoData,
      differentInvoiceRecipient,
      formattedDifferentInvoiceAddress,
      hasOnlyFwInDeregistration,
    };
  }),
  withMethods(store => {
    const updateStoreState = (storeStateValues: Partial<InitialRelocationState> & Partial<ConfirmationStatusState>) => {
      const relocationFromProducts = storeStateValues?.['relocationFromProducts'];
      const relocationFromAddress = storeStateValues?.['relocationFromAddress'];
      const relocationToAddress = storeStateValues?.['relocationToAddress'];
      const invoiceData = storeStateValues?.['invoiceData'] ?? store.invoiceData();
      const eRechnungsInfo = storeStateValues?.['eRechnungsInfo'] ?? store.eRechnungsInfo();
      const relocationDetails = storeStateValues?.['relocationDetails'] ?? store.relocationDetails();
      const eRechnungChangeIndices = storeStateValues?.['eRechnungChangeIndices'];
      const sepaChangeIndices = storeStateValues?.['sepaChangeIndices'];
      const whiteListedProducts = store._productsWhiteListLogic.removeNotWhiteListedProducts(
        relocationFromProducts ?? store.relocationFromProducts(),
      );

      const updatedStoreStateValues = {
        ...storeStateValues,
        relocationFromProducts: sortProductsByCategory(whiteListedProducts, true),
        relocationInitializedForFromAddress: relocationFromAddress !== store.relocationFromAddress(),
        relocationFromAddress: createAddress(relocationFromAddress ?? store.relocationFromAddress()),
        relocationToAddress: createAddress(relocationToAddress ?? store.relocationToAddress()),
        relocationDetails: sortProductsByCategory(updateRelocationDetails({ relocationDetails, invoiceData, eRechnungsInfo }), true),
        sepaChangeIndices,
        eRechnungChangeIndices,
      };

      patchState(store, { ...updatedStoreStateValues });
    };

    const initRelocationProcess = async (products: Product[]) => {
      if (!products || products.length === 0) {
        throw new Error('Products array cannot be empty or undefined');
      }

      const address = createAddress(products[0].address);
      const mappedAddresses = await store._addressGroupService.loadAndMapGroupNames([address]);
      updateStoreState({
        relocationFromAddress: address,
        relocationFromProducts: products,
        addressDetailsLinkType:
          mappedAddresses?.[0]?.addressGroup?.detailsLinkType &&
          isCancelledRelocationOrMoveOut(mappedAddresses?.[0]?.addressGroup?.detailsLinkType)
            ? null
            : mappedAddresses?.[0]?.addressGroup?.detailsLinkType,
        relocationInitializedForFromAddress: true,
      });

      const url = checkIfRelocationIsAllowed(products) ? RELOCATION_NEW_ADDRESS_ROUTE : RELOCATION_ERROR_ROUTE;
      await store._router.navigate([`/${RELOCATION_ROUTE}/${url}`]);
    };

    const initRelocationStatusProcess = async (address: Address) => {
      updateStoreState({
        relocationFromAddress: address,
      });

      await store._router.navigate([`/${RELOCATION_ROUTE}/${RELOCATION_STATUS_ROUTE}`]);
    };
    return {
      updateStoreState,
      initRelocationProcess,
      initRelocationStatusProcess,
    };
  }),
);
